import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, InputSignal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TransportMean } from '../../../interfaces/register-user.response';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseFormField } from '../../form/abstracts';
import { MatError } from '@angular/material/form-field';
import { TransportMeansFieldService } from './services/transport-means-field.service';

@Component({
  selector: 'app-transport-means-field',
  standalone: true,
  imports: [
    TranslateModule,
    MatError
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: TransportMeansFieldComponent
    },
  ],
  templateUrl: './transport-means-field.component.html',
  styleUrls: ['./transport-means-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportMeansFieldComponent extends BaseFormField<TransportMean[]> {
  // Inputs
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();

  // Services
  private _cdr = inject(ChangeDetectorRef);
  private _transportMeansFieldService = inject(TransportMeansFieldService);
  
  // Transport means options
  public transportMeans: TransportMean[] = this._transportMeansFieldService.getTransportMeansOptions();
    
  // Effects
  markAllFieldsAsTouchedForDesktop = effect(() => {
    const control = this._formControlName();
    this._transportMeansFieldService.markFieldsAsTouchedForDesktop(control, this._cdr);
  }, { allowSignalWrites: true });
  
  // Methods
  private _formControlName =  () => this.form().get(this.formControlName());
  
  /**
   * Handle chip set transport means change
   * Maintains exact original validation logic
   */
  public handleChipSetTransportMeansChange(event: Event) {
    const customEvent = event as CustomEvent<{ values: string[] }>;
    const selectedTransportMeans = customEvent.detail.values;
    const control = this.form().get(this.formControlName());
    
    this._transportMeansFieldService.handleTransportMeansSelection(selectedTransportMeans, control);
    
    this.form().get(this.formControlName())?.setValue(selectedTransportMeans);
    
    control?.markAsTouched();
    control?.markAsDirty();
  }
  
  /**
   * Check if field is invalid
   * Uses original _isLastSlideValidationActive validation logic
   */
  public isInvalidField(): boolean {
    const control = this.form().get(this.formControlName());
    return this._transportMeansFieldService.validateField(control);
  }
  
  /**
   * Check if transport mean is selected
   */
  public isSelectedTransportMean(value: string): boolean {
    return this._transportMeansFieldService.isSelectedTransportMean(value);
  }
}
